import styled, { css } from 'styled-components';
import { theme, palette, ifProp } from 'styled-tools';
import TextField from '../TextField';
import { HostPlatform } from '../../config/hostPlatform';

import Card from '../Card';

const isMonday = HostPlatform.isMonday();

export const ElipsisLoading = styled.div`
  display: inline-block;
  position: relative;
  margin: auto;
  margin-right: 20%;
  ${ifProp(
    'readOnly',
    css`
      margin-right: 25%;
    `
  )}
  div {
    position: absolute;
    top: -4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${palette('colorBody')};
    animation-timing-function: cubic-bezier(0, 1, 1, 0);
  }
  div:nth-child(1) {
    left: 8px;
    animation: lds-ellipsis1 0.6s infinite;
  }
  div:nth-child(2) {
    left: 8px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  div:nth-child(3) {
    left: 32px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  div:nth-child(4) {
    left: 56px;
    animation: lds-ellipsis3 0.6s infinite;
  }
  @keyframes lds-ellipsis1 {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes lds-ellipsis3 {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0);
    }
  }
  @keyframes lds-ellipsis2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(24px, 0);
    }
  }
`;

export const Timer = styled.div`
  font-size: ${theme('fontSize.xxs')};
  font-weight: bold;
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  padding: 6px 20px;
  margin: 0 8px 10px 0;
  background-color: ${({ color }) => palette(color)};
  border-radius: ${theme('borderRadius.sm')};
  transform: scale(1);
  transition: ${theme('transition')};
`;

export const ActionButton = styled.button`
  display: flex;
  margin: auto;
  color: ${palette('colorBody')};
  width: fit-content;
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}rem` : '1.5rem')};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

  ${({ noBackground }) =>
    noBackground &&
    css`
      background: none;
      border: none;
    `}

  svg {
    margin-bottom: -3px;
  }
`;

export const TimeButton = styled(ActionButton)`
  display: flex;
  color: ${palette('colorBody')};
  padding: 0 ${theme('spacing.lg')};
  float: right;

  ${theme('components.Button.sizes.md')}
  svg {
    margin: auto;
    height: 80%;
    width: 100%;
  }
  ${isMonday
    ? css`
        padding: 7px;
        height: 100%;
        svg {
          margin: auto;
          height: 100%;
          width: 100%;
        }
      `
    : null}

  span {
    line-height: 1;
    white-space: nowrap;
    margin: auto;
    margin-right: 7px;
    font-size: ${theme('fontSize.lg')};
  }

  &:hover {
    ${({ readOnly }) =>
      !readOnly
        ? css`
            background-color: ${palette('bgHoverGrey')};
            color: ${palette('colorBody')};
            border-radius: 4px;
          `
        : css`
            cursor: default;
          `}
  }
`;

export const Chip = styled.button`
  background-color: #d7e0fb;
  color: #5d6b96;
  border-radius: ${theme('borderRadius.md')};
  border: none;
  padding: 3px 5px;
  cursor: pointer;
  margin-left: 10px;
`;

export const RelativeTimerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: row;
  min-width: 267px;
  max-width: 267px;

  span {
    font-size: 1.5rem;
    color: ${palette('colorBody')};
    margin: auto 2px;
  }
`;

export const TimerPainelContainer = styled(Card)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${theme('spacing.sm')};
  z-index: 5;
  box-shadow: ${theme('shadow.lg')};

  &:after,
  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 90%;
    width: 0;
    height: 0;
    border-style: solid;
  }

  &:after {
    top: -19px;
    border-color: transparent transparent ${palette('popUp')} transparent;
    border-width: 10px;
  }

  &:before {
    top: -20px;
    border-color: transparent transparent ${palette('popUp')} transparent;
    border-width: 10px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 10px;
  padding-right: 15px;
  border-right: 1px solid grey;
  min-height: 50.5px;

  &:first-of-type {
    margin-left: 0;
  }

  #section-interval {
    display: flex;
    flex-direction: row;
  }
  #minute-chips {
    margin-top: ${theme('spacing.xs')};
  }
`;

export const FloatButton = styled.button`
  position: absolute;
  border-radius: ${theme('borderRadius.lg')};
  width: fit-content;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  color: ${palette('colorInvertBody')};
  background-color: ${palette('bgInvertBody')};

  svg {
    margin-bottom: -2px;
  }

  top: -10px;
  right: -40px;
`;

export const FloatButton2 = styled.button`
  position: absolute;
  border-radius: ${theme('borderRadius.lg')};
  width: fit-content;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  color: white;
  background-color: gray;

  svg {
    margin-bottom: -2px;
  }

  bottom: -20px;
  right: -20px;
`;

export const TimerInput = styled(TextField)`
  padding: 0 3px 0 3px;
  /* width: min-content; */
  text-align: center;
  font-size: ${theme('fontSize.lg')};
  font-weight: bold;
  color: ${theme('colorInput')};
  background-color: inherit;
  border-color: transparent;

  &:disabled {
    background-color: transparent;
    color: ${palette('colorBody')};
  }
`;
